'use client';

import { Stack, Typography } from '@mui/material';
import type { SqlEngine } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface DeleteSqlEngineDialogProps {
  engine: SqlEngine;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteSqlEngineDialog = ({
  engine,
  workspaceId,
  open,
  onClose,
  onDeleted,
}: DeleteSqlEngineDialogProps) => {
  const dal = useDAL();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await dal.lakehouse.deleteSqlEngine(workspaceId, engine.id);
      onDeleted();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete SQL engine'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Delete SQL Engine',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Delete',
          onClick: handleDelete,
          'data-testid': 'confirm-delete-sql-engine-btn',
          disabled: isLoading,
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-delete-sql-engine-btn',
        },
      }}
    >
      <Stack gap='16px' padding='0 40px'>
        <Typography>
          Are you sure you want to delete the SQL engine{' '}
          <strong>{engine.name}</strong>?
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          This action cannot be undone.
        </Typography>
        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
